import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { ListClusterRestrictionsResponse } from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

import { useSimulatedClusterDemoUi } from '@cast/infrastructure-demo';
import { apiClient } from '@cast/infrastructure-api';

const CLUSTER_RESTRICTIONS_QUERY_KEY = 'cluster-restrictions';

type ClusterRestrictionsQueryParams = Parameters<
  typeof apiClient.costReport.listClusterRestrictions
>[0];

/** With params → exact key; without → base key for prefix invalidation. */
export const getClusterRestrictionsQueryKey = (
  params?: ClusterRestrictionsQueryParams
) =>
  params
    ? ([CLUSTER_RESTRICTIONS_QUERY_KEY, params] as const)
    : ([CLUSTER_RESTRICTIONS_QUERY_KEY] as const);

const getClusterRestrictionsQueryOptions = ({
  enabled = true,
  isSimulatedClusterDemoUi,
  ...params
}: ClusterRestrictionsQueryParams & {
  enabled?: boolean;
  isSimulatedClusterDemoUi: boolean;
}) =>
  infiniteQueryOptions({
    queryKey: getClusterRestrictionsQueryKey(params),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.costReport.listClusterRestrictions({
        ...params,
        ...(pageParam ? { 'page.cursor': pageParam } : {}),
      });
      return (
        data || {
          items: [],
          nextPageCursor: undefined,
        }
      );
    },
    enabled: makeIsEnabled(
      enabled,
      !!params.organizationId,
      !!params.clusterId,
      !isSimulatedClusterDemoUi
    ),
    initialPageParam: '',
    getNextPageParam: (lastPage: ListClusterRestrictionsResponse) => {
      return lastPage.nextPageCursor || undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

type UseClusterRestrictionsQueryArgs = ClusterRestrictionsQueryParams & {
  enabled?: boolean;
};

export const useClusterRestrictionsQuery = ({
  enabled = true,
  clusterId,
  ...params
}: UseClusterRestrictionsQueryArgs) => {
  const isSimulatedClusterDemoUi = useSimulatedClusterDemoUi();

  const {
    isLoading,
    isFetching,
    data: restrictionsResponse,
    error,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery(
    getClusterRestrictionsQueryOptions({
      enabled,
      isSimulatedClusterDemoUi,
      clusterId,
      ...params,
    })
  );

  return {
    isLoading,
    isFetching,
    data: {
      items: flatMap(restrictionsResponse?.pages, (page) => page.items || []),
      nextPageCursor:
        restrictionsResponse?.pages[restrictionsResponse.pages.length - 1]
          ?.nextPageCursor,
    } as ListClusterRestrictionsResponse,
    error,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    refetch,
  };
};
